import React, { useEffect, useState } from 'react';
import { useAuth, SignIn } from '@clerk/clerk-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLoadingState from '../components/AdminLoadingState.jsx';

/**
 * AdminSignIn component for admin-specific sign-in flow
 */
const AdminSignIn = () => {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);

  const redirectUrl = searchParams.get('redirect_url') || '/admin';
  const message = searchParams.get('message');

  // Check admin status after sign in
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!isLoaded || !isSignedIn || checking) return;

      setChecking(true);
      try {
        const token = await getToken();
        if (!token) {
          setError('Authentication failed. Please try again.');
          return;
        }

        const response = await fetch('https://parrotmate.com/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const userData = await response.json();
          if (userData.is_admin) {
            // User is admin, redirect to intended destination
            navigate(redirectUrl, { replace: true });
          } else {
            // User is not admin
            setError('Access denied. Admin privileges required.');
          }
        } else {
          setError('Failed to verify admin access. Please try again.');
        }
      } catch (err) {
        console.error('Admin access check failed:', err);
        setError('An error occurred while checking admin access.');
      } finally {
        setChecking(false);
      }
    };

    checkAdminAccess();
  }, [isLoaded, isSignedIn, getToken, navigate, redirectUrl, checking]);

  // Show loading state while checking admin access
  if (checking) {
    return (
      <AdminLoadingState 
        message="Verifying admin access..." 
        fullScreen={true}
      />
    );
  }

  // Show error if admin check failed
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="text-center">
            <div className="text-red-400 text-4xl mb-4">🚫</div>
            <h1 className="text-2xl font-bold text-white mb-4 dark:text-white">Access Denied</h1>
            <p className="text-gray-300 mb-6 dark:text-gray-600">{error}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors dark:text-white"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors dark:text-white"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img src="/logo.svg" alt="ParrotMate Logo" className="h-12 w-12 mr-3" />
            <h1 className="text-3xl font-bold text-teal-400">ParrotMate</h1>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2 dark:text-white">Admin Access</h2>
          <p className="text-gray-400">
            Sign in with your administrator account to access the admin panel.
          </p>
        </div>

        {/* Session expired message */}
        {message === 'session_expired' && (
          <div className="mb-6 p-4 bg-yellow-900 border border-yellow-600 rounded-lg">
            <div className="flex items-center">
              <div className="text-yellow-400 mr-3">⚠️</div>
              <div>
                <p className="text-yellow-200 font-medium">Session Expired</p>
                <p className="text-yellow-300 text-sm">
                  Your admin session has expired due to inactivity. Please sign in again.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Clerk SignIn component */}
        <div className="bg-gray-800 rounded-lg shadow-lg p-6">
          <SignIn 
            fallbackRedirectUrl={redirectUrl}
            appearance={{
              elements: {
                rootBox: "w-full",
                card: "bg-transparent shadow-none",
                headerTitle: "text-white",
                headerSubtitle: "text-gray-400",
                socialButtonsBlockButton: "bg-gray-700 border-gray-600 text-white hover:bg-gray-600",
                formButtonPrimary: "bg-teal-600 hover:bg-teal-700",
                formFieldInput: "bg-gray-700 border-gray-600 text-white",
                formFieldLabel: "text-gray-300",
                identityPreviewText: "text-gray-300",
                formResendCodeLink: "text-teal-400 hover:text-teal-300",
                footerActionLink: "text-teal-400 hover:text-teal-300"
              }
            }}
          />
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-400">
            Need help? Contact your system administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminSignIn;
